import React, { useState } from "react";
import Container from "@mui/material/Container";
import { useNavigate } from "react-router-dom";

interface HelpItem {
    id: string;
    question: string;
    answer: string;
}

const helpData: HelpItem[] = [
    {
        id: "help2",
        question: "How do I participate in campaigns?",
        answer: "Browse available campaigns in the app, follow the campaign instructions carefully, and submit your proof (screenshots/videos). Once verified, you'll receive your incentive to your registered UPI ID.",
    },
    {
        id: "help3",
        question: "What types of campaigns are available?",
        answer: "You can participate in various campaigns such as app installations, surveys, feature exploration, and more. Each campaign has specific instructions that you need to follow for successful completion.",
    },

    {
        id: "help4",
        question: "How long does verification and payment take?",
        answer: "Campaign submissions are verified and processed within 24-72 hours. Payment timelines may vary based on submission volume. You can track your submission status in the app.",
    },
    {
        id: "help5",
        question: "What payment methods are supported?",
        answer: "We currently support UPI (Unified Payments Interface) for transfers to your registered bank account. Enter your UPI ID when requested to receive incentive payouts.",
    },
    {
        id: "help6",
        question: "Why was my campaign submission rejected?",
        answer: "Submissions may be rejected if the proof doesn't meet the campaign requirements specified in the instructions. Make sure to carefully follow all guidelines and submit clear, valid proof.",
    },
    {
        id: "help7",
        question: "Is there a minimum withdrawal amount?",
        answer: "No, there's no minimum withdrawal amount. You can request a payout as soon as you have any verified incentive amount in your account.",
    },
    {
        id: "help8",
        question: "How do I contact support?",
        answer: "You can reach our support team by emailing support@koinapp.co. We typically respond within 24 hours. You can also use the in-app chat feature for quick assistance.",
    },
    {
        id: "help9",
        question: "Is the app safe and secure?",
        answer: "Yes, we implementsecurity measures to protect your personal and payment information. We never share your data with third parties without your consent.",
    },
    {
        id: "help10",
        question: "Can I cancel a campaign after starting?",
        answer: "You can abandon a campaign at any time, but you'll only receive an incentive if you complete it fully and submit valid proof. Incomplete campaigns won't earn you any rewards.",
    },
];

export default function HelpPage() {
    const [expanded, setExpanded] = useState<string | false>(false);
    const navigate = useNavigate();

    const handleChange = (panel: string) => {
        setExpanded((prev) => (prev === panel ? false : panel));
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <Container
                maxWidth="md"
                sx={{
                    pt: 3,
                    pb: 4,
                }}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 16L6 10L12 4"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">Help & Support</h1>
                    <div className="w-20" />
                </div>

                {/* Search/Filter Info */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-orange-900">
                        📌 Can't find what you're looking for? Contact us at{" "}
                        <span className="font-semibold">support@koinapp.co</span>
                    </p>
                </div>

                {/* FAQ Items */}
                <div className="space-y-3">
                    {helpData.map(({ id, question, answer }) => {
                        const isOpen = expanded === id;

                        return (
                            <div
                                key={id}
                                className={`border rounded-xl bg-white transition-all duration-300 overflow-hidden ${isOpen
                                        ? "border-orange-400 shadow-md"
                                        : "border-gray-200 shadow-sm hover:shadow-md"
                                    }`}
                            >
                                <button
                                    onClick={() => handleChange(id)}
                                    className="w-full flex items-center justify-between p-4 text-left bg-white focus:outline-none group"
                                >
                                    <span className="text-base text-gray-900 font-medium pr-3">
                                        {question}
                                    </span>
                                    <div className="flex-shrink-0">
                                        {isOpen ? (
                                            <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-orange-600 text-white">
                                                <svg
                                                    width="12"
                                                    height="12"
                                                    viewBox="0 0 12 12"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M1 1L11 11M1 11L11 1"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>
                                        ) : (
                                            <div className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 text-gray-600 group-hover:border-orange-400 group-hover:text-orange-600 transition-colors">
                                                <svg
                                                    width="12"
                                                    height="12"
                                                    viewBox="0 0 12 12"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M6 1V11M1 6H11"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </button>

                                <div
                                    className={`transition-all duration-300 ease-in-out bg-gray-50 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                                        }`}
                                >
                                    <div className="px-4 py-4 text-gray-700 leading-relaxed text-sm">
                                        {answer}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer CTA */}
                <div className="mt-8 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 text-center">
                    <p className="text-gray-700 text-sm mb-3">
                        Still have questions?
                    </p>
                    <a
                        href="mailto:support@koinapp.co"
                        className="inline-block px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
                    >
                        Email Support
                    </a>
                </div>
            </Container>
        </div>
    );
}
